import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  InputAdornment,
  IconButton,
  Chip,
  Avatar,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Divider,
  Alert,
  CircularProgress,
  Container,
  Tooltip,
  Collapse,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  ListItemButton
} from '@mui/material';

// Icons
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import TimelineIcon from '@mui/icons-material/Timeline';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import PersonIcon from '@mui/icons-material/Person';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import UpdateIcon from '@mui/icons-material/Update';
import InfoIcon from '@mui/icons-material/Info';
import AppRegistrationIcon from '@mui/icons-material/AppRegistration';
import BusinessIcon from '@mui/icons-material/Business';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import HistoryIcon from '@mui/icons-material/History';
import VisibilityIcon from '@mui/icons-material/Visibility';
import GroupIcon from '@mui/icons-material/Group';
import DashboardIcon from '@mui/icons-material/Dashboard';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import DeleteIcon from '@mui/icons-material/Delete';
import AppsIcon from '@mui/icons-material/Apps';
import ListIcon from '@mui/icons-material/List';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import WarningIcon from '@mui/icons-material/Warning';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import LabelIcon from '@mui/icons-material/Label';
import LinkIcon from '@mui/icons-material/Link';
import AssignmentIcon from '@mui/icons-material/Assignment';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';

import { useMsal } from '@azure/msal-react';
import { Client } from '@microsoft/microsoft-graph-client';
import { Application } from '@microsoft/microsoft-graph-types';
import { useAppCache } from '../context/AppCacheContext';
import { InteractionRequiredAuthError, AuthenticationResult } from '@azure/msal-browser';

interface AppLifecycleDetailsProps {
  onCancel: () => void;
}

interface LifecycleInfo {
  application: Application;
  owners: any[];
  createdBy?: any;
  servicePrincipal?: any;
  lastModified?: string;
  publisherDomain?: string;
  signInAudience?: string;
}

interface LifecycleMetric {
  id: string;
  title: string;
  count: number;
  icon: React.ReactNode;
  color: 'primary' | 'secondary' | 'success' | 'warning' | 'error' | 'info';
  description: string;
  apps: Application[];
}

const AppLifecycleDetails: React.FC<AppLifecycleDetailsProps> = ({ onCancel }) => {
  const { instance, accounts } = useMsal();
  const { applications, spMap, appOwners, currentUserId } = useAppCache(); 

  // console.log(applications.map(app => ({ id: app.id, createdDateTime: app.createdDateTime })));

  const [searchQuery, setSearchQuery] = useState('');
  const [selectedApp, setSelectedApp] = useState<Application | null>(null);
  const [lifecycleInfo, setLifecycleInfo] = useState<LifecycleInfo | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dashboardExpanded, setDashboardExpanded] = useState(true);
  const [requiresConsent, setRequiresConsent] = useState(false);
  
  // New state for metric dialog functionality
  const [metricDialogOpen, setMetricDialogOpen] = useState(false);
  const [selectedMetric, setSelectedMetric] = useState<LifecycleMetric | null>(null);

  const filteredApps = applications.filter(app =>
    app.displayName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    app.appId?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Check if user has sufficient permissions
  const hasSufficientPermissions = (tokenResponse: AuthenticationResult) => {
    const scopes = tokenResponse.scopes || [];
    const requiredScopes = ['Application.Read.All', 'Directory.Read.All', 'User.Read.All'];
    return requiredScopes.every(scope => 
      scopes.some(userScope => 
        userScope === scope || 
        userScope === 'Application.ReadWrite.All' || 
        userScope === 'Directory.AccessAsUser.All' ||
        userScope === 'User.ReadWrite.All'
      )
    );
  };

  const inDetailRef = useRef(false);

  // Enhanced lifecycle metrics using only reliable cached data
  const lifecycleMetrics = React.useMemo((): LifecycleMetric[] => {
    const metrics: LifecycleMetric[] = [];

    // Total applications
    metrics.push({
      id: 'total-apps',
      title: 'Total Applications',
      count: applications.length,
      icon: <AppsIcon />,
      color: 'primary',
      description: 'All applications in your tenant',
      apps: applications
    });

    // Applications with Owners (using cached ownership data)
    const appsWithOwners = applications.filter(app => {
      const ownerInfo = appOwners[app.appId || app.id || ''];
      return ownerInfo && ownerInfo.owners.length > 0;
    });

    metrics.push({
      id: 'apps-with-owners',
      title: 'Applications with Owners',
      count: appsWithOwners.length,
      icon: <PersonIcon />,
      color: 'success',
      description: 'Applications that have assigned owners',
      apps: appsWithOwners
    });

    // Applications without Owners (using cached ownership data)
    const appsWithoutOwners = applications.filter(app => {
      const ownerInfo = appOwners[app.appId || app.id || ''];
      return !ownerInfo || ownerInfo.owners.length === 0;
    });

    metrics.push({
      id: 'apps-without-owners',
      title: 'Applications without Owners',
      count: appsWithoutOwners.length,
      icon: <GroupIcon />,
      color: 'error',
      description: 'Applications that need owner assignment',
      apps: appsWithoutOwners
    });

    // Applications with multiple platforms
    const multiPlatformApps = applications.filter(app => {
      const platforms = [];
      if (app.spa?.redirectUris?.length) platforms.push('spa');
      if (app.web?.redirectUris?.length) platforms.push('web');
      if (app.publicClient?.redirectUris?.length) platforms.push('mobile');
      return platforms.length > 1;
    });

    if (multiPlatformApps.length > 0) {
      metrics.push({
        id: 'multi-platform',
        title: 'Multi-Platform Apps',
        count: multiPlatformApps.length,
        icon: <BusinessIcon />,
        color: 'secondary',
        description: 'Applications supporting multiple platforms',
        apps: multiPlatformApps
      });
    }

    // Applications with API permissions (for integration insights)
    const appsWithApiPermissions = applications.filter(app => 
      app.requiredResourceAccess && app.requiredResourceAccess.length > 0
    );

    if (appsWithApiPermissions.length > 0) {
      metrics.push({
        id: 'api-integrations',
        title: 'API Integrations',
        count: appsWithApiPermissions.length,
        icon: <AdminPanelSettingsIcon />,
        color: 'info',
        description: 'Applications with Microsoft Graph or other API permissions',
        apps: appsWithApiPermissions
      });
    }

    // Applications exposing APIs
    const appsExposingApis = applications.filter(app =>
      app.api?.oauth2PermissionScopes && app.api.oauth2PermissionScopes.length > 0
    );

    if (appsExposingApis.length > 0) {
      metrics.push({
        id: 'api-providers',
        title: 'API Providers',
        count: appsExposingApis.length,
        icon: <TrendingUpIcon />,
        color: 'warning',
        description: 'Applications that expose APIs for other apps to consume',
        apps: appsExposingApis
      });
    }

    const now = new Date();
    
    // NEW: Applications with expiring credentials (within 30 days or expired)
    const appsWithExpiringCredentials = applications.filter(app => {
      const credentials = app.passwordCredentials || [];
      return credentials.some(cred => {
        if (!cred.endDateTime) return false;
        const endDate = new Date(cred.endDateTime);
        const daysLeft = Math.floor((endDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
        return daysLeft < 30; // Includes expired (negative values)
      });
    });

    if (appsWithExpiringCredentials.length > 0) {
      metrics.push({
        id: 'expiring-credentials',
        title: 'Expiring Credentials',
        count: appsWithExpiringCredentials.length,
        icon: <WarningIcon />,
        color: 'error',
        description: 'Applications with credentials expiring within 30 days or already expired',
        apps: appsWithExpiringCredentials
      });
    }
    const daysAgo = (dateStr: string | null | undefined, days: number) => {
      if (!dateStr) return false;
      const date = new Date(dateStr);
      return (now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24) <= days;
    };

    const appsWithCreatedDate = applications.filter(app => !!app.createdDateTime);

    const appsCreated7Days = appsWithCreatedDate.filter(app => daysAgo(app.createdDateTime, 7));
    const appsCreated30Days = appsWithCreatedDate.filter(app => daysAgo(app.createdDateTime, 30));

    const oldestApp = appsWithCreatedDate.reduce((oldest, app) => {
      const appDate = typeof app.createdDateTime === 'string' ? app.createdDateTime : null;
      const oldestDate = oldest && typeof oldest.createdDateTime === 'string' ? oldest.createdDateTime : null;
      
      if (!oldest || (appDate && oldestDate && new Date(appDate) < new Date(oldestDate))) {
        return app;
      }
      return oldest;
    }, null as Application | null);

    const newestApp = appsWithCreatedDate.reduce((newest, app) => {
      const appDate = typeof app.createdDateTime === 'string' ? app.createdDateTime : null;
      const newestDate = newest && typeof newest.createdDateTime === 'string' ? newest.createdDateTime : null;
      
      if (!newest || (appDate && newestDate && new Date(appDate) > new Date(newestDate))) {
        return app;
      }
      return newest;
    }, null as Application | null);

    const avgAppAgeDays = appsWithCreatedDate.length > 0
      ? Math.round(appsWithCreatedDate.reduce((sum, app) => {
          const createdDate = typeof app.createdDateTime === 'string' ? app.createdDateTime : null;
          if (createdDate) {
            const created = new Date(createdDate);
            return sum + ((now.getTime() - created.getTime()) / (1000 * 60 * 60 * 24));
          }
          return sum;
        }, 0) / appsWithCreatedDate.length)
      : 0;

    metrics.push({
      id: 'apps-created-7-days',
      title: 'Apps Created (7 Days)',
      count: appsCreated7Days.length,
      icon: <CalendarTodayIcon />,
      color: 'info',
      description: 'Applications registered in the last 7 days',
      apps: appsCreated7Days
    });
    metrics.push({
      id: 'apps-created-30-days',
      title: 'Apps Created (30 Days)',
      count: appsCreated30Days.length,
      icon: <CalendarTodayIcon />,
      color: 'primary',
      description: 'Applications registered in the last 30 days',
      apps: appsCreated30Days
    });
    metrics.push({
      id: 'oldest-app',
      title: 'Oldest App',
      count: 1,
      icon: <HistoryIcon />,
      color: 'warning',
      description: oldestApp?.displayName
        ? `Oldest registration: ${oldestApp.displayName} (${typeof oldestApp.createdDateTime === 'string' ? new Date(oldestApp.createdDateTime).toLocaleDateString() : 'Unknown date'})`
        : 'No data',
      apps: oldestApp ? [oldestApp] : []
    });
    metrics.push({
      id: 'newest-app',
      title: 'Newest App',
      count: 1,
      icon: <TrendingUpIcon />,
      color: 'success',
      description: newestApp?.displayName
        ? `Newest registration: ${newestApp.displayName} (${typeof newestApp.createdDateTime === 'string' ? new Date(newestApp.createdDateTime).toLocaleDateString() : 'Unknown date'})`
        : 'No data',
      apps: newestApp ? [newestApp] : []
    });
    metrics.push({
      id: 'avg-app-age',
      title: 'Average App Age',
      count: avgAppAgeDays,
      icon: <UpdateIcon />,
      color: 'secondary',
      description: `Average age of apps: ${avgAppAgeDays} days`,
      apps: []
    });    

    return metrics;
  }, [applications, appOwners]);

  // Handle metric click to open dialog
  const handleMetricClick = (metric: LifecycleMetric) => {
    if (metric.count === 0) return;
    setSelectedMetric(metric);
    setMetricDialogOpen(true);
  };

  // Enhanced fetchLifecycleInfo with optimized token handling
  const fetchLifecycleInfo = async (app: Application) => {
    if (!app.id) return;

    setLoading(true);
    setError(null);
    setRequiresConsent(false);

    const cachedOwnerInfo = appOwners[app.appId || app.id || ''];
    const cachedOwners = cachedOwnerInfo ? cachedOwnerInfo.owners : [];    

    try {
      // Check if we have cached ownership data first
      const cachedOwnerInfo = appOwners[app.appId || app.id || ''];
      const cachedOwners = cachedOwnerInfo ? cachedOwnerInfo.owners : [];

      // If we have cached data, use it and avoid Graph calls
      if (cachedOwners.length >= 0) { // >= 0 to include apps with no owners
        console.log(`Using cached lifecycle data for app: ${app.displayName}`);
        
        // Create a mock service principal from cached spMap data
        let servicePrincipal = null;
        if (app.appId && spMap[app.appId]) {
          servicePrincipal = {
            id: `sp-${app.appId}`,
            appId: app.appId,
            displayName: app.displayName,
            createdDateTime: app.createdDateTime,
            servicePrincipalType: 'Application'
          };
        }

        setLifecycleInfo({
          application: app,
          owners: cachedOwners,
          servicePrincipal,
          lastModified: app.createdDateTime || undefined,
          publisherDomain: app.publisherDomain || undefined,
          signInAudience: app.signInAudience || undefined,
        });

        setLoading(false);
        return;
      }

      // If no cached data available, try to get fresh data
      const requiredScopes = ["Application.Read.All", "Directory.Read.All", "User.Read.All"];
      let tokenResponse: AuthenticationResult;
      
      try {
        // Try silent token acquisition first
        tokenResponse = await instance.acquireTokenSilent({
          scopes: requiredScopes,
          account: accounts[0]
        });

        // Check if we have sufficient permissions
        if (!hasSufficientPermissions(tokenResponse)) {
          console.log('Token does not contain sufficient permissions, requesting interactive consent');
          setRequiresConsent(true);
          
          tokenResponse = await instance.acquireTokenPopup({
            scopes: requiredScopes,
            prompt: "consent",
            account: accounts[0]
          });
          
          setRequiresConsent(false);
        }

      } catch (silentError: any) {
        console.log('Silent token acquisition failed:', silentError);
        
        // Only request interactive auth if it's actually needed
        if (silentError instanceof InteractionRequiredAuthError || 
            silentError.errorCode === 'interaction_required' ||
            silentError.errorCode === 'consent_required' ||
            silentError.message?.includes('AADSTS65001')) {
          
          console.log('Interaction required - requesting user consent');
          setRequiresConsent(true);
          
          try {
            tokenResponse = await instance.acquireTokenPopup({
              scopes: requiredScopes,
              prompt: "consent",
              account: accounts[0]
            });
            
            console.log('Interactive authentication successful');
            setRequiresConsent(false);
          } catch (interactiveError: any) {
            console.error('Interactive authentication failed:', interactiveError);
            
            if (interactiveError.errorCode === 'user_cancelled') {
              setError('Permission consent was cancelled. Using cached data where available.');
              // Fall back to cached data even if incomplete
              setLifecycleInfo({
                application: app,
                owners: [],
                servicePrincipal: null,
                lastModified: app.createdDateTime || undefined,
                publisherDomain: app.publisherDomain || undefined,
                signInAudience: app.signInAudience || undefined,
              });
              setLoading(false);
              return;
            } else {
              setError(`Authentication failed: ${interactiveError.message || 'Please try again or contact your administrator.'}`);
            }
            setLoading(false);
            return;
          }
        } else {
          throw silentError;
        }
      }

      // Initialize Graph client with the acquired token
      const client = Client.init({
        authProvider: (done) => done(null, tokenResponse.accessToken)
      });

      console.log('Fetching fresh lifecycle information for app:', app.displayName);

      // Fetch owners
      let owners = [];
      try {
        console.log('Fetching application owners...');
        const ownersResponse = await client.api(`/applications/${app.id}/owners`).get();
        owners = ownersResponse.value || [];
        console.log(`Found ${owners.length} owners`);
      } catch (ownersError: any) {
        console.warn('Could not fetch owners:', ownersError);
      }

      // Fetch service principal
      let servicePrincipal = null;
      try {
        console.log('Fetching service principal...');
        const spResponse = await client
          .api('/servicePrincipals')
          .filter(`appId eq '${app.appId}'`)
          .select('id,appId,displayName,createdDateTime,servicePrincipalType')
          .get();
        servicePrincipal = spResponse.value?.[0] || null;
        console.log('Service principal found:', !!servicePrincipal);
      } catch (spError: any) {
        console.warn('Could not fetch service principal:', spError);
      }

      setLifecycleInfo({
        application: app,
        owners,
        servicePrincipal,
        lastModified: app.createdDateTime || undefined,
        publisherDomain: app.publisherDomain || undefined,
        signInAudience: app.signInAudience || undefined,
      });

      console.log('Lifecycle information successfully retrieved');

    } catch (err: any) {
      console.error('Error fetching lifecycle info:', err);
      
      let errorMessage = 'Failed to fetch lifecycle information: ';
      
      if (err.code === 'Forbidden' || err.status === 403) {
        errorMessage += 'Insufficient permissions. Using cached data where available.';
        // Show cached data even with permissions error
        setLifecycleInfo({
          application: app,
          owners: cachedOwners,
          servicePrincipal: null,
          lastModified: app.createdDateTime || undefined,
          publisherDomain: app.publisherDomain || undefined,
          signInAudience: app.signInAudience || undefined,
        });
      } else if (err.code === 'Unauthorized' || err.status === 401) {
        errorMessage += 'Authentication failed. Please try signing out and signing back in.';
      } else if (err.message?.includes('network')) {
        errorMessage += 'Network error. Please check your connection and try again.';
      } else {
        errorMessage += err.message || 'An unexpected error occurred.';
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const handlePop = (e: PopStateEvent) => {
      const s = (e.state || {}) as any;
      if (s?.actionView === 'lifecycle') {
        if (s.lifecycleView === 'detail' && s.lifecycleAppId) {
          // restore detail if needed
          if (!selectedApp || (selectedApp.id !== s.lifecycleAppId && selectedApp.appId !== s.lifecycleAppId)) {
            const found = applications.find(a =>
              a.id === s.lifecycleAppId || a.appId === s.lifecycleAppId
            );
            if (found) {
              setSelectedApp(found);
              fetchLifecycleInfo(found);
              inDetailRef.current = true;
            }
          }
        } else {
          // list view inside lifecycle
            if (inDetailRef.current) {
              inDetailRef.current = false;
              setSelectedApp(null);
              setLifecycleInfo(null);
              setError(null);
            }
        }
      }
      // if actionView changes (leaving lifecycle), ActionsContent will unmount this component
    };
    window.addEventListener('popstate', handlePop);
    return () => window.removeEventListener('popstate', handlePop);
  }, [applications, selectedApp, fetchLifecycleInfo, setSelectedApp]);

  // CHANGE: wrap existing handleAppSelect logic
  const handleAppSelect = (app: Application) => {
    console.log('Selected app for lifecycle analysis:', app.displayName);
    setSelectedApp(app);
    fetchLifecycleInfo(app);
    const baseState = (window.history.state || {}) as any;
    const detailState = {
      ...baseState,
      actionView: 'lifecycle',
      lifecycleView: 'detail',
      lifecycleAppId: app.id || app.appId
    };
    if (!inDetailRef.current) {
      window.history.pushState(detailState, '');
      inDetailRef.current = true;
    } else {
      // switching between apps while already in detail
      window.history.replaceState(detailState, '');
    }
  };

  const handleRetryConsent = () => {
    if (selectedApp) {
      console.log('Retrying with consent for app:', selectedApp.displayName);
      fetchLifecycleInfo(selectedApp);
    }
  };

  const formatDate = (dateString?: string | null) => {
    if (!dateString) return 'Not available';
    try {
      return new Date(dateString).toLocaleString();
    } catch {
      return 'Invalid date';
    }
  };

  const getAppTypeChips = (app: Application) => {
    const types = [];
    
    if (app.spa?.redirectUris?.length) {
      types.push({ label: 'SPA', color: '#2196f3', bgColor: 'rgba(33, 150, 243, 0.1)' });
    }
    if (app.web?.redirectUris?.length) {
      types.push({ label: 'Web', color: '#4caf50', bgColor: 'rgba(76, 175, 80, 0.1)' });
    }
    if (app.api?.oauth2PermissionScopes?.length) {
      types.push({ label: 'API', color: '#ff9800', bgColor: 'rgba(255, 152, 0, 0.1)' });
    }
    if (app.appRoles?.length) {
      types.push({ label: 'App Roles', color: '#9c27b0', bgColor: 'rgba(156, 39, 176, 0.1)' });
    }
    
    return types;
  };

  const getAudienceInfo = (signInAudience?: string) => {
    const audienceMap = {
      'AzureADMyOrg': { label: 'Single Tenant', color: '#4caf50', icon: <BusinessIcon /> },
      'AzureADMultipleOrgs': { label: 'Multi-Tenant', color: '#ff9800', icon: <GroupIcon /> },
      'AzureADandPersonalMicrosoftAccount': { label: 'Multi-Tenant + Personal', color: '#f44336', icon: <GroupIcon /> },
      'PersonalMicrosoftAccount': { label: 'Personal Accounts', color: '#9c27b0', icon: <PersonIcon /> }
    };
    
    return audienceMap[signInAudience as keyof typeof audienceMap] || 
           { label: signInAudience || 'Unknown', color: '#757575', icon: <InfoIcon /> };
  };

  // NEW: Get credential health information
  const getCredentialHealth = (app: Application) => {
    const credentials = app.passwordCredentials || [];
    const now = new Date();
    
    const credentialStatus = credentials.map(cred => {
      const endDate = cred.endDateTime ? new Date(cred.endDateTime) : null;
      if (!endDate) return { name: cred.displayName || 'Secret', status: 'unknown', daysLeft: null, color: '#757575' };
      
      const daysLeft = Math.floor((endDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
      
      if (daysLeft < 0) {
        return { name: cred.displayName || 'Secret', status: 'expired', daysLeft, color: '#d32f2f', icon: <WarningIcon /> };
      } else if (daysLeft < 30) {
        return { name: cred.displayName || 'Secret', status: 'expiring-soon', daysLeft, color: '#f57c00', icon: <WarningIcon /> };
      } else if (daysLeft < 90) {
        return { name: cred.displayName || 'Secret', status: 'expiring', daysLeft, color: '#ffa726', icon: <InfoIcon /> };
      } else {
        return { name: cred.displayName || 'Secret', status: 'healthy', daysLeft, color: '#4caf50', icon: <CheckCircleIcon /> };
      }
    });
    
    return { credentials: credentialStatus, count: credentials.length };
  };

  // NEW: Check if current user is owner
  const isCurrentUserOwner = (app: Application) => {
    if (!currentUserId) return false;
    const ownerInfo = appOwners[app.appId || app.id || ''];
    return ownerInfo?.owners.some(owner => owner.id === currentUserId) || false;
  };

  // NEW: Get optional claims summary
  const getOptionalClaimsSummary = (app: Application) => {
    const claims = app.optionalClaims;
    if (!claims) return { count: 0, types: [] };
    
    const types = [];
    let count = 0;
    
    if (claims.idToken && claims.idToken.length > 0) {
      types.push(`${claims.idToken.length} ID token`);
      count += claims.idToken.length;
    }
    if (claims.accessToken && claims.accessToken.length > 0) {
      types.push(`${claims.accessToken.length} access token`);
      count += claims.accessToken.length;
    }
    if (claims.saml2Token && claims.saml2Token.length > 0) {
      types.push(`${claims.saml2Token.length} SAML2 token`);
      count += claims.saml2Token.length;
    }
    
    return { count, types };
  };

  if (selectedApp && lifecycleInfo) {
    // Detailed view remains the same as before
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 6 }}>
        {/* Header */}
        <Card variant="outlined" sx={{ mb: 4, borderRadius: 2, overflow: 'hidden' }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              p: 2,
              backgroundColor: '#f3e8ff',
              borderBottom: '1px solid rgba(124, 58, 237, 0.2)'
            }}
          >
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={() => {
              if (inDetailRef.current) {
                // go back to list (pops detail state)
                window.history.back();
              } else {
                // fallback
                setSelectedApp(null);
                setLifecycleInfo(null);
                setError(null);
              }
            }}
            sx={{
              mr: 2,
              borderRadius: 2,
              pl: 1.5,
              color: "text.secondary",
              borderColor: "divider",
              "&:hover": {
                backgroundColor: "rgba(0,0,0,0.04)",
                borderColor: "primary.main",
                color: "primary.main"
              }
            }}
          >
            Back to Apps
          </Button>
            
            <Box
              sx={{
                borderLeft: '4px solid #7c3aed',
                pl: 2,
                py: 0.5,
              }}
            >
              <Typography 
                variant="h4" 
                sx={{ 
                  fontWeight: 600,
                  color: '#7c3aed',
                  fontFamily: "'Segoe UI', Roboto, 'Open Sans', sans-serif",
                  letterSpacing: '-0.01em',
                }}
              >
                Application Lifecycle
              </Typography>
              
              <Typography 
                variant="body2" 
                sx={{ 
                  color: 'text.secondary',
                  fontFamily: "'Segoe UI', Roboto, 'Open Sans', sans-serif",
                }}
              >
                {selectedApp.displayName}
              </Typography>
            </Box>
          </Box>
        </Card>

        {/* Loading State */}
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 4 }}>
            <CircularProgress sx={{ mr: 2 }} />
            <Typography variant="body1">
              {requiresConsent ? 'Requesting permissions...' : 'Loading lifecycle information...'}
            </Typography>
          </Box>
        )}

        {/* Error State with Retry Option */}
        {error && (
          <Alert 
            severity={error.includes('cached data') ? 'warning' : 'error'}
            sx={{ mb: 3, borderRadius: 2 }}
            action={
              error.includes('consent') ? (
                <Button 
                  color="inherit" 
                  size="small" 
                  onClick={handleRetryConsent}
                  disabled={loading}
                >
                  Grant Permissions
                </Button>
              ) : (
                <Button 
                  color="inherit" 
                  size="small" 
                  onClick={() => handleAppSelect(selectedApp)}
                  disabled={loading}
                >
                  Retry
                </Button>
              )
            }
          >
            {error}
          </Alert>
        )}

        {!loading && (
          <Grid container spacing={3}>
            {/* Application Overview */}
            <Grid item xs={12} lg={8}>
              <Card variant="outlined" sx={{ borderRadius: 2, mb: 3 }}>
                <CardContent sx={{ p: 0 }}>
                  <Typography
                    variant="h6"
                    sx={{
                      px: 3,
                      py: 2,
                      borderBottom: '1px solid rgba(0,0,0,0.12)',
                      backgroundColor: '#f9f9f9',
                      fontWeight: 600,
                      display: 'flex',
                      alignItems: 'center'
                    }}
                  >
                    <AppRegistrationIcon sx={{ mr: 1, color: 'primary.main' }} />
                    Application Overview
                  </Typography>
                  
                  <Box sx={{ p: 3 }}>
                    <Grid container spacing={3}>
                      <Grid item xs={12} md={6}>
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                            Display Name
                          </Typography>
                          <Typography variant="h6" sx={{ fontWeight: 500 }}>
                            {lifecycleInfo.application.displayName || 'Unknown Application'}
                          </Typography>
                        </Box>
                        
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                            Application ID
                          </Typography>
                          <Typography variant="body1" sx={{ fontFamily: 'monospace', fontSize: '0.9rem' }}>
                            {lifecycleInfo.application.appId}
                          </Typography>
                        </Box>

                        <Box sx={{ mb: 2 }}>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                            Object ID
                          </Typography>
                          <Typography variant="body1" sx={{ fontFamily: 'monospace', fontSize: '0.9rem' }}>
                            {lifecycleInfo.application.id}
                          </Typography>
                        </Box>
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                            Application Types
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                            {getAppTypeChips(lifecycleInfo.application).map((type, index) => (
                              <Chip
                                key={index}
                                label={type.label}
                                size="small"
                                sx={{
                                  backgroundColor: type.bgColor,
                                  color: type.color,
                                  fontWeight: 500,
                                  border: `1px solid ${type.color}20`
                                }}
                              />
                            ))}
                            {getAppTypeChips(lifecycleInfo.application).length === 0 && (
                              <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                No specific type configured
                              </Typography>
                            )}
                          </Box>
                        </Box>

                        <Box sx={{ mb: 2 }}>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                            Sign-in Audience
                          </Typography>
                          {(() => {
                            const audienceInfo = getAudienceInfo(lifecycleInfo.signInAudience);
                            return (
                              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <Box sx={{ mr: 1, color: audienceInfo.color, display: 'flex' }}>
                                  {audienceInfo.icon}
                                </Box>
                                <Typography variant="body1">
                                  {audienceInfo.label}
                                </Typography>
                              </Box>
                            );
                          })()}
                        </Box>

                        <Box sx={{ mb: 2 }}>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                            Publisher Domain
                          </Typography>
                          <Typography variant="body1">
                            {lifecycleInfo.publisherDomain || 'Not specified'}
                          </Typography>
                        </Box>
                      </Grid>

                      {/* NEW: Additional Information Section */}
                      <Grid item xs={12}>
                        <Divider sx={{ my: 2 }} />
                      </Grid>

                      {/* NEW: Ownership Status Badge */}
                      {isCurrentUserOwner(lifecycleInfo.application) && (
                        <Grid item xs={12}>
                          <Alert severity="info" icon={<VerifiedUserIcon />} sx={{ borderRadius: 2 }}>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              You are an owner of this application
                            </Typography>
                          </Alert>
                        </Grid>
                      )}

                      {/* NEW: Credential Health */}
                      {(() => {
                        const credHealth = getCredentialHealth(lifecycleInfo.application);
                        if (credHealth.count === 0) return null;
                        
                        return (
                          <Grid item xs={12} md={6}>
                            <Box sx={{ mb: 2 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                <VpnKeyIcon sx={{ mr: 1, color: 'text.secondary', fontSize: '1.2rem' }} />
                                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                                  Credential Health
                                </Typography>
                              </Box>
                              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                {credHealth.credentials.map((cred, index) => (
                                  <Box 
                                    key={index}
                                    sx={{ 
                                      display: 'flex', 
                                      alignItems: 'center',
                                      p: 1,
                                      borderRadius: 1,
                                      backgroundColor: `${cred.color}10`,
                                      border: `1px solid ${cred.color}40`
                                    }}
                                  >
                                    <Box sx={{ color: cred.color, display: 'flex', mr: 1 }}>
                                      {cred.icon}
                                    </Box>
                                    <Box sx={{ flexGrow: 1 }}>
                                      <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.85rem' }}>
                                        {cred.name}
                                      </Typography>
                                      <Typography variant="caption" sx={{ color: cred.color }}>
                                        {cred.status === 'expired' && 'Expired'}
                                        {cred.status === 'expiring-soon' && `Expires in ${cred.daysLeft} days`}
                                        {cred.status === 'expiring' && `Expires in ${cred.daysLeft} days`}
                                        {cred.status === 'healthy' && `Valid for ${cred.daysLeft} days`}
                                        {cred.status === 'unknown' && 'No expiration date'}
                                      </Typography>
                                    </Box>
                                  </Box>
                                ))}
                              </Box>
                            </Box>
                          </Grid>
                        );
                      })()}

                      {/* NEW: Application Tags */}
                      {lifecycleInfo.application.tags && lifecycleInfo.application.tags.length > 0 && (
                        <Grid item xs={12} md={6}>
                          <Box sx={{ mb: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                              <LabelIcon sx={{ mr: 1, color: 'text.secondary', fontSize: '1.2rem' }} />
                              <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                                Tags
                              </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                              {lifecycleInfo.application.tags.map((tag, index) => (
                                <Chip
                                  key={index}
                                  label={tag}
                                  size="small"
                                  sx={{
                                    backgroundColor: 'rgba(156, 39, 176, 0.1)',
                                    color: '#9c27b0',
                                    fontWeight: 500,
                                    fontSize: '0.75rem',
                                    border: '1px solid rgba(156, 39, 176, 0.3)'
                                  }}
                                />
                              ))}
                            </Box>
                          </Box>
                        </Grid>
                      )}

                      {/* NEW: Identifier URIs */}
                      {lifecycleInfo.application.identifierUris && lifecycleInfo.application.identifierUris.length > 0 && (
                        <Grid item xs={12} md={6}>
                          <Box sx={{ mb: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                              <LinkIcon sx={{ mr: 1, color: 'text.secondary', fontSize: '1.2rem' }} />
                              <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                                Identifier URIs
                              </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                              {lifecycleInfo.application.identifierUris.map((uri, index) => (
                                <Typography 
                                  key={index} 
                                  variant="body2" 
                                  sx={{ 
                                    fontFamily: 'monospace', 
                                    fontSize: '0.8rem',
                                    backgroundColor: '#f5f5f5',
                                    p: 0.5,
                                    borderRadius: 1,
                                    wordBreak: 'break-all'
                                  }}
                                >
                                  {uri}
                                </Typography>
                              ))}
                            </Box>
                          </Box>
                        </Grid>
                      )}

                      {/* NEW: Optional Claims Summary */}
                      {(() => {
                        const claimsSummary = getOptionalClaimsSummary(lifecycleInfo.application);
                        if (claimsSummary.count === 0) return null;
                        
                        return (
                          <Grid item xs={12} md={6}>
                            <Box sx={{ mb: 2 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                <AssignmentIcon sx={{ mr: 1, color: 'text.secondary', fontSize: '1.2rem' }} />
                                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                                  Optional Claims
                                </Typography>
                              </Box>
                              <Box sx={{ 
                                p: 1.5,
                                borderRadius: 1,
                                backgroundColor: 'rgba(33, 150, 243, 0.05)',
                                border: '1px solid rgba(33, 150, 243, 0.2)'
                              }}>
                                <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.5 }}>
                                  {claimsSummary.count} claim{claimsSummary.count === 1 ? '' : 's'} configured
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {claimsSummary.types.join(', ')}
                                </Typography>
                              </Box>
                            </Box>
                          </Grid>
                        );
                      })()}

                    </Grid>
                  </Box>
                </CardContent>
              </Card>

              {/* Timeline */}
              <Card variant="outlined" sx={{ borderRadius: 2 }}>
                <CardContent sx={{ p: 0 }}>
                  <Typography
                    variant="h6"
                    sx={{
                      px: 3,
                      py: 2,
                      borderBottom: '1px solid rgba(0,0,0,0.12)',
                      backgroundColor: '#f9f9f9',
                      fontWeight: 600,
                      display: 'flex',
                      alignItems: 'center'
                    }}
                  >
                    <HistoryIcon sx={{ mr: 1, color: 'primary.main' }} />
                    Lifecycle Timeline
                  </Typography>
                  
                  <Box sx={{ p: 3 }}>
                    <List sx={{ width: '100%' }}>
                      {/* Application Creation */}
                      <ListItem alignItems="flex-start" sx={{ px: 0 }}>
                        <ListItemAvatar>
                          <Avatar sx={{ bgcolor: '#4caf50' }}>
                            <AppRegistrationIcon />
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                Application Created
                              </Typography>
                              <Chip label="Creation" size="small" color="success" variant="outlined" />
                            </Box>
                          }
                          secondary={
                            <Box sx={{ mt: 1 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                                <CalendarTodayIcon sx={{ fontSize: '1rem', mr: 0.5, color: 'text.secondary' }} />
                                <Typography variant="body2" color="text.secondary">
                                  {formatDate(lifecycleInfo.application.createdDateTime)}
                                </Typography>
                              </Box>
                              <Typography variant="body2" sx={{ mt: 1 }}>
                                Application registration was created in Azure AD
                              </Typography>
                            </Box>
                          }
                        />
                      </ListItem>

                      <Divider variant="inset" component="li" />

                      {/* Service Principal Creation */}
                      {lifecycleInfo.servicePrincipal && (
                        <>
                          <ListItem alignItems="flex-start" sx={{ px: 0 }}>
                            <ListItemAvatar>
                              <Avatar sx={{ bgcolor: '#2196f3' }}>
                                <AdminPanelSettingsIcon />
                              </Avatar>
                            </ListItemAvatar>
                            <ListItemText
                              primary={
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                    Service Principal Created
                                  </Typography>
                                  <Chip label="Enterprise App" size="small" color="primary" variant="outlined" />
                                </Box>
                              }
                              secondary={
                                <Box sx={{ mt: 1 }}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                                    <CalendarTodayIcon sx={{ fontSize: '1rem', mr: 0.5, color: 'text.secondary' }} />
                                    <Typography variant="body2" color="text.secondary">
                                      {formatDate(lifecycleInfo.servicePrincipal.createdDateTime)}
                                    </Typography>
                                  </Box>
                                  <Typography variant="body2" sx={{ mt: 1 }}>
                                    Enterprise application instance created for this tenant
                                  </Typography>
                                </Box>
                              }
                            />
                          </ListItem>
                          <Divider variant="inset" component="li" />
                        </>
                      )}

                      {/* Current Status */}
                      <ListItem alignItems="flex-start" sx={{ px: 0 }}>
                        <ListItemAvatar>
                          <Avatar sx={{ bgcolor: '#ff9800' }}>
                            <UpdateIcon />
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                Current Status
                              </Typography>
                              <Chip label="Active" size="small" color="warning" variant="outlined" />
                            </Box>
                          }
                          secondary={
                            <Box sx={{ mt: 1 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                                <UpdateIcon sx={{ fontSize: '1rem', mr: 0.5, color: 'text.secondary' }} />
                                <Typography variant="body2" color="text.secondary">
                                  Last verified: {new Date().toLocaleString()}
                                </Typography>
                              </Box>
                              <Typography variant="body2" sx={{ mt: 1 }}>
                                Application is currently active and accessible
                              </Typography>
                            </Box>
                          }
                        />
                      </ListItem>
                    </List>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Ownership & Management */}
            <Grid item xs={12} lg={4}>
              <Card variant="outlined" sx={{ borderRadius: 2, mb: 3 }}>
                <CardContent sx={{ p: 0 }}>
                  <Typography
                    variant="h6"
                    sx={{
                      px: 3,
                      py: 2,
                      borderBottom: '1px solid rgba(0,0,0,0.12)',
                      backgroundColor: '#f9f9f9',
                      fontWeight: 600,
                      display: 'flex',
                      alignItems: 'center'
                    }}
                  >
                    <PersonIcon sx={{ mr: 1, color: 'primary.main' }} />
                    Ownership & Management
                  </Typography>
                  
                  <Box sx={{ p: 3 }}>
                    {lifecycleInfo.owners.length > 0 ? (
                      <List dense>
                        {lifecycleInfo.owners.map((owner, index) => (
                          <ListItem key={index} sx={{ px: 0 }}>
                            <ListItemAvatar>
                              <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main' }}>
                                <AccountCircleIcon fontSize="small" />
                              </Avatar>
                            </ListItemAvatar>
                            <ListItemText
                              primary={owner.displayName || owner.userPrincipalName || 'Unknown User'}
                              secondary={
                                owner.userPrincipalName
                                  ? owner.userPrincipalName
                                  : owner.mail
                                    ? owner.mail
                                    : owner.id
                              }
                              primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
                              secondaryTypographyProps={{ variant: 'caption' }}
                            />
                          </ListItem>
                        ))}
                      </List>
                    ) : (
                      <Box sx={{ textAlign: 'center', py: 2 }}>
                        <PersonIcon sx={{ fontSize: '3rem', color: 'text.disabled', mb: 1 }} />
                        <Typography variant="body2" color="text.secondary">
                          No owners assigned
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          This application has no explicitly assigned owners
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </CardContent>
              </Card>

              {/* Quick Stats */}
              <Card variant="outlined" sx={{ borderRadius: 2 }}>
                <CardContent sx={{ p: 0 }}>
                  <Typography
                    variant="h6"
                    sx={{
                      px: 3,
                      py: 2,
                      borderBottom: '1px solid rgba(0,0,0,0.12)',
                      backgroundColor: '#f9f9f9',
                      fontWeight: 600,
                      display: 'flex',
                      alignItems: 'center'
                    }}
                  >
                    <InfoIcon sx={{ mr: 1, color: 'primary.main' }} />
                    Quick Statistics
                  </Typography>
                  
                  <Box sx={{ p: 3 }}>
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <Box sx={{ textAlign: 'center', p: 1, borderRadius: 1, bgcolor: 'rgba(76, 175, 80, 0.1)' }}>
                          <Typography variant="h6" sx={{ fontWeight: 600, color: '#4caf50' }}>
                            {lifecycleInfo.owners.length}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Owners
                          </Typography>
                        </Box>
                      </Grid>
                      
                      <Grid item xs={6}>
                        <Box sx={{ textAlign: 'center', p: 1, borderRadius: 1, bgcolor: 'rgba(33, 150, 243, 0.1)' }}>
                          <Typography variant="h6" sx={{ fontWeight: 600, color: '#2196f3' }}>
                            {lifecycleInfo.application.requiredResourceAccess?.length || 0}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            API Permissions
                          </Typography>
                        </Box>
                      </Grid>

                      <Grid item xs={6}>
                        <Box sx={{ textAlign: 'center', p: 1, borderRadius: 1, bgcolor: 'rgba(255, 152, 0, 0.1)' }}>
                          <Typography variant="h6" sx={{ fontWeight: 600, color: '#ff9800' }}>
                            {(lifecycleInfo.application.web?.redirectUris?.length || 0) + 
                             (lifecycleInfo.application.spa?.redirectUris?.length || 0)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Redirect URIs
                          </Typography>
                        </Box>
                      </Grid>

                      <Grid item xs={6}>
                        <Box sx={{ textAlign: 'center', p: 1, borderRadius: 1, bgcolor: 'rgba(156, 39, 176, 0.1)' }}>
                          <Typography variant="h6" sx={{ fontWeight: 600, color: '#9c27b0' }}>
                            {lifecycleInfo.application.appRoles?.length || 0}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            App Roles
                          </Typography>
                        </Box>
                      </Grid>
                    </Grid>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}
      </Container>
    );
  }

  // App Selection View with Enhanced Dashboard
  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 6 }}>
      {/* Header */}
      <Card variant="outlined" sx={{ mb: 4, borderRadius: 2, overflow: 'hidden' }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            p: 2,
            backgroundColor: '#f3e8ff',
            borderBottom: '1px solid rgba(124, 58, 237, 0.2)'
          }}
        >
          <Button 
            variant="outlined" 
            startIcon={<ArrowBackIcon />}
            onClick={onCancel}
            sx={{
              mr: 2,
              borderRadius: 2,
              pl: 1.5,
              color: "text.secondary",
              borderColor: "divider",
              "&:hover": {
                backgroundColor: "rgba(0,0,0,0.04)",
                borderColor: "primary.main",
                color: "primary.main"
              }
            }}
          >
            Back
          </Button>
          
          <Box
            sx={{
              borderLeft: '4px solid #7c3aed',
              pl: 2,
              py: 0.5,
            }}
          >
            <Typography 
              variant="h4" 
              sx={{ 
                fontWeight: 600,
                color: '#7c3aed',
                fontFamily: "'Segoe UI', Roboto, 'Open Sans', sans-serif",
                letterSpacing: '-0.01em',
              }}
            >
              View Application Lifecycle
            </Typography>
            
            <Typography 
              variant="body2" 
              sx={{ 
                color: 'text.secondary',
                fontFamily: "'Segoe UI', Roboto, 'Open Sans', sans-serif",
              }}
            >
              Track creation, ownership, and change history for applications
            </Typography>
          </Box>
        </Box>
      </Card>

      {/* Enhanced Lifecycle Dashboard with Click Functionality */}
      <Card variant="outlined" sx={{ mb: 4, borderRadius: 2 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            p: 2,
            backgroundColor: '#f8f0ff',
            borderBottom: '1px solid rgba(0,0,0,0.12)',
            cursor: 'pointer'
          }}
          onClick={() => setDashboardExpanded(!dashboardExpanded)}
        >
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <DashboardIcon sx={{ mr: 1, color: '#7c3aed' }} />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Lifecycle Dashboard
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Chip
              size="small"
              icon={<ListIcon />}
              label="Clickable Metrics"
              variant="outlined"
              color="secondary"
            />
            <IconButton size="small">
              {dashboardExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </Box>
        </Box>
        
        <Collapse in={dashboardExpanded}>
          <CardContent sx={{ p: 3 }}>
            <Grid container spacing={2}>
              {lifecycleMetrics.map((metric) => (
                <Grid item xs={12} sm={6} md={4} lg={3} key={metric.id}>
                  <Paper
                    variant="outlined"
                    onClick={() => handleMetricClick(metric)}
                    sx={{
                      p: 2,
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      cursor: metric.count > 0 ? 'pointer' : 'default',
                      borderColor: `${metric.color}.light`,
                      backgroundColor: `rgba(${
                        metric.color === 'primary' ? '33,150,243' :
                        metric.color === 'secondary' ? '156,39,176' :
                        metric.color === 'success' ? '76,175,80' :
                        metric.color === 'warning' ? '255,152,0' :
                        metric.color === 'error' ? '244,67,54' :
                        '33,150,243'
                      }, 0.05)`,
                      transition: 'all .2s',
                      '&:hover': metric.count > 0 ? {
                        boxShadow: 2,
                        transform: 'translateY(-1px)',
                        borderColor: `${metric.color}.main`
                      } : {}
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 1 }}>
                      <Box
                        sx={{
                          mr: 1.5,
                          color: `${metric.color}.main`
                        }}
                      >
                        {metric.icon}
                      </Box>
                      <Box sx={{ flexGrow: 1 }}>
                        <Typography
                          variant="h4"
                          sx={{
                            fontWeight: 700,
                            lineHeight: 1,
                            fontSize: { xs: '1.7rem', sm: '1.9rem' },
                            color: `${metric.color}.main`
                          }}
                        >
                          {metric.count}
                        </Typography>
                      </Box>
                    </Box>
                    <Typography
                      variant="subtitle2"
                      sx={{ fontWeight: 600, mb: 1, fontSize: '0.8rem' }}
                    >
                      {metric.title}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{ mb: 1.5, flexGrow: 1, color: 'text.secondary' }}
                    >
                      {metric.description}
                    </Typography>
                    {metric.count > 0 && (
                      <Typography
                        variant="caption"
                        sx={{ textAlign: 'center', mt: 0.75, color: `${metric.color}.main`, fontWeight: 500 }}
                      >
                        Click to list apps
                      </Typography>
                    )}
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </CardContent>
        </Collapse>
      </Card>

      {/* Application Search */}
      <Card variant="outlined" sx={{ mb: 4, borderRadius: 2 }}>
        <CardContent sx={{ p: 3 }}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
            Select Application to View Lifecycle
          </Typography>
          
          <TextField
            label="Search Applications"
            variant="outlined"
            fullWidth
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search by app name or App ID..."
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
              endAdornment: searchQuery && (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="clear search"
                    onClick={() => setSearchQuery('')}
                    edge="end"
                  >
                    <ClearIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{
              mb: 3,
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                '&:hover fieldset': {
                  borderColor: '#7c3aed',
                },
              }
            }}
          />

          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {filteredApps.length === 0 
              ? "No applications found" 
              : `Showing ${filteredApps.length} application${filteredApps.length === 1 ? '' : 's'}`
            }
          </Typography>

          {filteredApps.length > 0 && (
            <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f3e8ff' }}>
                    <TableCell sx={{ fontWeight: 600 }}>Application Name</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>App ID</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Created</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Type</TableCell>
                    <TableCell sx={{ fontWeight: 600, width: 120 }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredApps.map((app) => (
                    <TableRow 
                      key={app.id}
                      sx={{ 
                        '&:hover': { backgroundColor: 'rgba(124, 58, 237, 0.04)' }
                      }}
                    >
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {app.displayName || 'Unknown Application'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                          {app.appId}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {formatDate(typeof app.createdDateTime === 'string' ? app.createdDateTime : undefined)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                          {getAppTypeChips(app).slice(0, 2).map((type, index) => (
                            <Chip
                              key={index}
                              label={type.label}
                              size="small"
                              sx={{
                                backgroundColor: type.bgColor,
                                color: type.color,
                                fontSize: '0.7rem',
                                height: '20px'
                              }}
                            />
                          ))}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="contained"
                          size="small"
                          startIcon={<VisibilityIcon />}
                          onClick={() => handleAppSelect(app)}
                          sx={{ 
                            borderRadius: 2,
                            textTransform: 'none',
                            backgroundColor: '#7c3aed',
                            '&:hover': { backgroundColor: '#6d28d9' }
                          }}
                        >
                          View
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {filteredApps.length === 0 && (
            <Box sx={{ textAlign: 'center', py: 4, backgroundColor: '#f5f5f5', borderRadius: 2 }}>
              <TimelineIcon sx={{ fontSize: 60, color: 'text.disabled', mb: 2 }} />
              <Typography variant="h6" color="text.secondary">
                No applications found
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Try adjusting your search terms or clear the filter
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Info Card */}
      <Card variant="outlined" sx={{ borderRadius: 2, bgcolor: '#f9f9f9' }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <InfoIcon color="primary" sx={{ mr: 1 }} />
            <Typography variant="h6">About Application Lifecycle</Typography>
          </Box>
          
          <Grid container spacing={3}>
            <Grid item xs={12} md={3}>
              <Box sx={{ p: 2, bgcolor: 'rgba(232, 245, 233, 0.5)', borderRadius: 1, height: '100%' }}>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: '#2e7d32' }}>
                  Creation & Timeline
                </Typography>
                <Typography variant="body2">
                  Track when applications were created and view important 
                  lifecycle events in chronological order.
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={3}>
              <Box sx={{ p: 2, bgcolor: 'rgba(227, 242, 253, 0.5)', borderRadius: 1, height: '100%' }}>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: '#1976d2' }}>
                  Ownership & Management
                </Typography>
                <Typography variant="body2">
                  View current owners and managers to understand 
                  responsibility and contact points.
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={3}>
              <Box sx={{ p: 2, bgcolor: 'rgba(243, 232, 255, 0.5)', borderRadius: 1, height: '100%' }}>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: '#7c3aed' }}>
                  Compliance & Auditing
                </Typography>
                <Typography variant="body2">
                  Essential for compliance and governance to understand 
                  application creation and management history.
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={3}>
              <Box sx={{ p: 2, bgcolor: 'rgba(255, 243, 224, 0.5)', borderRadius: 1, height: '100%' }}>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: '#f57c00' }}>
                  Cached Data Optimized
                </Typography>
                <Typography variant="body2">
                  Uses cached ownership data for fast dashboard loading. 
                  Fresh data fetched only when viewing individual apps.
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Metric Dialog - Similar to AttackSurfaceOverview */}
      <Dialog
        open={metricDialogOpen}
        onClose={() => setMetricDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pr: 1 }}>
          <span>
            {selectedMetric?.title} — {selectedMetric?.count} app{selectedMetric?.count === 1 ? '' : 's'}
          </span>
          <Button
            onClick={() => setMetricDialogOpen(false)}
            variant="outlined"
            size="medium"
            sx={{
              minWidth: 0,
              px: 1.5,
              py: 0.5,
              ml: 2,
              color: 'text.secondary',
              borderRadius: 2,
              fontWeight: 500,
              textTransform: 'none'
            }}
          >
            Close
          </Button>
        </DialogTitle>
        <DialogContent dividers>
          <List dense>
            {selectedMetric?.apps.map(app => (
              <ListItem
                key={app.id}
                disablePadding
                sx={{
                  borderRadius: 1,
                  mb: 0.5,
                }}
              >
                <ListItemButton
                  onClick={() => {
                    setMetricDialogOpen(false);
                    handleAppSelect(app);
                  }}
                  sx={{
                    borderRadius: 1,
                    '&:hover': { backgroundColor: 'rgba(124, 58, 237, 0.04)' }
                  }}
                >
                  <ListItemText
                    primary={app.displayName}
                    secondary={`App ID: ${app.appId}`}
                  />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </DialogContent>
      </Dialog>
    </Container>
  );
};

export default AppLifecycleDetails;